/**
 * Application Error Diagnostics (Feature 39)
 *
 * Single source of truth for turning any caught error into something the user
 * can act on and the team can trace:
 *
 *  - a title (kept from the legacy per-page alert text),
 *  - a plain Dutch explanation of the likely cause,
 *  - a concrete action hint,
 *  - a short, quotable debug code (e.g. `bevestig-3f9a`),
 *  - plus structured logging of the raw error (including its Firestore `code`)
 *    and an aggregate GA4 `app_error` event.
 *
 * Pure module: no I/O and no side effects, so every mapping is unit-testable.
 * Only low-cardinality codes/contexts ever leave the browser — never messages,
 * names or email addresses.
 */

import { trackAppError } from './analytics';

/** Stable error codes used by {@link describeAppError} and reported as `error_code`. */
export const APP_ERROR_CODES = {
  PERMISSION_DENIED: 'permission-denied',
  OFFLINE: 'unavailable',
  TIMEOUT: 'deadline-exceeded',
  CONVIVENTIE_MISSING: 'conviventie-missing',
  TARGET_MISSING: 'target-missing',
  DOCUMENT_TOO_LARGE: 'document-too-large',
  INVALID_DATA: 'invalid-data',
  UNKNOWN: 'unknown'
};

/** Call-site contexts, used for the debug code and the GA4 `error_context`. */
export const APP_ERROR_CONTEXTS = {
  CONVIVENTIES_LOAD: 'conviventies-laden',
  CONVIVENTIE_SAVE: 'conviventie-opslaan',
  CONVIVENTIE_DELETE: 'conviventie-verwijderen',
  GUEST_GROUP_CREATE: 'gastengroep-aanmaken',
  LEDEN_LOAD: 'leden-laden',
  CONFIRM_LEDEN: 'bevestig-leden',
  LEDEN_UPLOAD: 'leden-upload',
  LEDEN_DOWNLOAD: 'leden-download',
  LEDEN_DELETE: 'leden-verwijderen'
};

/**
 * Legacy alert titles per context. Errors we cannot classify keep the exact
 * wording the app used before, so nothing regresses visually.
 */
const LEGACY_TITLES = {
  [APP_ERROR_CONTEXTS.CONVIVENTIES_LOAD]: 'Fout bij het laden van conviventies',
  [APP_ERROR_CONTEXTS.CONVIVENTIE_SAVE]: 'Fout bij het opslaan van conviventie',
  [APP_ERROR_CONTEXTS.CONVIVENTIE_DELETE]: 'Fout bij het verwijderen van conviventie',
  [APP_ERROR_CONTEXTS.GUEST_GROUP_CREATE]: 'Fout bij het aanmaken van gastengroep',
  [APP_ERROR_CONTEXTS.LEDEN_LOAD]: 'Fout bij het laden van leden',
  [APP_ERROR_CONTEXTS.CONFIRM_LEDEN]: 'Fout bij het bevestigen van leden',
  [APP_ERROR_CONTEXTS.LEDEN_UPLOAD]: 'Fout bij het uploaden van de ledenlijst',
  [APP_ERROR_CONTEXTS.LEDEN_DOWNLOAD]: 'Fout bij het downloaden van de ledenlijst',
  [APP_ERROR_CONTEXTS.LEDEN_DELETE]: 'Fout bij het verwijderen van de ledenlijst'
};

const DEFAULT_TITLE = 'Er ging iets mis';

/**
 * Failure modes that are worth another attempt: they are transient by nature
 * (network blips, server hiccups) and resolved by simply retrying.
 */
const RETRYABLE_CODES = new Set([
  APP_ERROR_CODES.OFFLINE,
  APP_ERROR_CODES.TIMEOUT,
  'cancelled'
]);

const RETRY_DELAYS = [500, 1500];

const getAppVersion = () => process.env.REACT_APP_VERSION || '';

/**
 * Normalises a Firebase/Firestore error code.
 * The modular SDK reports plain codes (`unavailable`), the legacy namespaced SDK
 * prefixed them (`firestore/unavailable`); both are accepted.
 *
 * @param {*} error
 * @returns {string} The bare code, or '' when unavailable
 */
export const normalizeFirestoreCode = (error) => {
  const raw = error?.code;
  if (typeof raw !== 'string' || raw === '') return '';
  return raw.startsWith('firestore/') ? raw.slice('firestore/'.length) : raw;
};

/**
 * Builds a tagged error so services can signal an application-level cause the
 * UI is able to react to.
 *
 * @param {string} appCode - One of {@link APP_ERROR_CODES}
 * @param {string} message - Human readable message (kept in the logs)
 * @param {Object} [details] - Extra, non-PII context
 * @returns {Error} An Error carrying `appCode` plus the given details
 */
export const createAppError = (appCode, message, details = {}) => {
  const error = new Error(message);
  error.appCode = appCode;
  Object.assign(error, details);
  return error;
};

/**
 * Returns the most specific code we know for an error.
 *
 * @param {*} error
 * @returns {string} Firestore code first, then `appCode`, else ''
 */
export const getErrorCode = (error) => normalizeFirestoreCode(error) || error?.appCode || '';

/**
 * Whether another attempt could plausibly succeed. Unknown/plain errors are not
 * retryable, so tests and user actions never incur an avoidable delay.
 *
 * @param {*} error
 * @returns {boolean}
 */
export const isRetryableError = (error) => RETRYABLE_CODES.has(getErrorCode(error));

const hashString = (value) => {
  let hash = 0x811c9dc5;
  const text = String(value || '');
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
};

/**
 * Deterministic short code the user can quote in a screenshot or message.
 * The same failure always produces the same code.
 *
 * @param {string} context - One of {@link APP_ERROR_CONTEXTS}
 * @param {string} code - The resolved error code
 * @returns {string} e.g. `bevestig-3f9a`
 */
export const buildDebugId = (context, code) => {
  const shortContext = String(context || 'fout').split('-')[0].slice(0, 12) || 'fout';
  return `${shortContext}-${hashString(`${context}|${code}`).slice(0, 4)}`;
};

/**
 * User-facing explanations per resolved code (Dutch, matching the app language).
 */
const INFO_BY_CODE = {
  [APP_ERROR_CODES.PERMISSION_DENIED]: {
    title: 'Geen toegang',
    message: 'Je hebt geen toegang (meer) tot deze gegevens. Mogelijk is je account of je toegang gewijzigd.',
    hint: 'Herlaad de pagina. Blijft het misgaan, vraag een beheerder je toegang te controleren.'
  },
  [APP_ERROR_CODES.OFFLINE]: {
    title: 'Geen verbinding',
    message: 'De app kon de server niet bereiken.',
    hint: 'Controleer je internetverbinding en probeer het opnieuw.'
  },
  [APP_ERROR_CODES.TIMEOUT]: {
    title: 'Verbinding time-out',
    message: 'De server reageerde niet op tijd.',
    hint: 'Probeer het opnieuw.'
  },
  [APP_ERROR_CODES.CONVIVENTIE_MISSING]: {
    title: 'Conviventie niet gevonden',
    message: 'Deze conviventie bestaat niet meer.',
    hint: 'De lijst wordt automatisch vernieuwd.'
  },
  [APP_ERROR_CODES.TARGET_MISSING]: {
    title: 'Gemeenschap niet gevonden',
    message: 'Deze gemeenschap of gastengroep is niet meer uitgenodigd voor deze conviventie (mogelijk aangepast door een beheerder).',
    hint: 'De lijst wordt automatisch vernieuwd.'
  },
  [APP_ERROR_CODES.DOCUMENT_TOO_LARGE]: {
    title: 'Te veel gegevens',
    message: 'De bevestiging is te groot om op te slaan.',
    hint: 'Bevestig minder gemeenschappen tegelijk en meld dit met de code hieronder.'
  },
  [APP_ERROR_CODES.INVALID_DATA]: {
    title: 'Ongeldige gegevens',
    message: 'Er ontbreekt een verplicht veld in deze ledenlijst.',
    hint: 'Controleer de ledenlijst in het tabblad Leden.'
  }
};

/** Fallback text for errors we cannot classify (title stays the legacy one). */
const UNKNOWN_INFO = {
  message: 'De oorzaak kon niet automatisch worden vastgesteld.',
  hint: 'Probeer het opnieuw. Blijft het misgaan, meld de code hieronder aan de beheerder.'
};

const classifyInvalidArgument = (error) => {
  const message = typeof error?.message === 'string' ? error.message : '';
  if (/entity size|too large/i.test(message)) return APP_ERROR_CODES.DOCUMENT_TOO_LARGE;
  return APP_ERROR_CODES.INVALID_DATA;
};

/**
 * Resolves the single code used for display, retry decisions and telemetry.
 *
 * @param {*} error
 * @returns {string} One of {@link APP_ERROR_CODES}
 */
export const resolveCode = (error) => {
  const appCode = typeof error?.appCode === 'string' ? error.appCode : '';
  if (
    appCode === APP_ERROR_CODES.CONVIVENTIE_MISSING ||
    appCode === APP_ERROR_CODES.TARGET_MISSING ||
    appCode === APP_ERROR_CODES.DOCUMENT_TOO_LARGE
  ) {
    return appCode;
  }

  const firestoreCode = normalizeFirestoreCode(error);
  if (firestoreCode === APP_ERROR_CODES.PERMISSION_DENIED) return APP_ERROR_CODES.PERMISSION_DENIED;
  if (firestoreCode === APP_ERROR_CODES.OFFLINE) return APP_ERROR_CODES.OFFLINE;
  if (firestoreCode === APP_ERROR_CODES.TIMEOUT || firestoreCode === 'cancelled') {
    return APP_ERROR_CODES.TIMEOUT;
  }
  if (firestoreCode === 'invalid-argument') return classifyInvalidArgument(error);

  return appCode || APP_ERROR_CODES.UNKNOWN;
};

/**
 * Maps an error to everything the UI needs, without losing the legacy title for
 * errors we do not recognise.
 *
 * @param {*} error - The caught error (Firestore error, tagged app error, …)
 * @param {string} [context] - One of {@link APP_ERROR_CONTEXTS}
 * @param {{ fallbackTitle?: string }} [options] - Override for the title
 * @returns {{ code: string, context: string, title: string, message: string, hint: string, retryable: boolean, debugId: string }}
 */
export const describeAppError = (error, context = 'unknown', { fallbackTitle } = {}) => {
  const code = resolveCode(error);
  const specific = INFO_BY_CODE[code];
  const legacyTitle = LEGACY_TITLES[context] || DEFAULT_TITLE;
  const info = specific || UNKNOWN_INFO;

  return {
    code,
    context,
    title: specific ? specific.title : fallbackTitle || legacyTitle,
    message: info.message,
    hint: info.hint,
    retryable: RETRYABLE_CODES.has(code),
    debugId: buildDebugId(context, code)
  };
};

/**
 * Renders one string for `alert()`: title, reason, hint and the quotable code.
 *
 * @param {Object} info - Result of {@link describeAppError}
 * @returns {string}
 */
export const formatAppErrorAlert = (info) => {
  if (!info) return '';

  const lines = [info.title];
  if (info.message) lines.push('', info.message);
  if (info.hint) lines.push('', info.hint);
  if (info.debugId) lines.push('', `(code ${info.debugId})`);

  return lines.join('\n');
};

/**
 * Reports a caught error.
 *
 * Keeps the raw error (and therefore its Firestore `code`) in the console — the
 * piece of information that was missing from the bug report — and sends one
 * aggregate GA4 `app_error` event carrying only the low-cardinality code,
 * context, retryability and the app version.
 *
 * Never throws: error reporting must not mask the error it reports.
 *
 * @param {Object} info - Result of {@link describeAppError}
 * @param {{ error?: *, meta?: Object }} [options] - Raw error and non-PII context
 */
export const reportAppError = (info, { error, meta } = {}) => {
  if (!info) return;

  const appVersion = getAppVersion();

  try {
    console.error('[app-error]', {
      code: info.code,
      context: info.context,
      title: info.title,
      retryable: Boolean(info.retryable),
      debugId: info.debugId,
      appVersion,
      meta: meta || null,
      error
    });
  } catch (loggingError) {
    // Swallow: never let logging replace the original failure.
  }

  try {
    Promise.resolve(
      trackAppError({
        error_code: info.code,
        error_context: info.context,
        retryable: Boolean(info.retryable),
        app_version: appVersion
      })
    ).catch(() => {
      // Swallow: telemetry is best-effort.
    });
  } catch (analyticsError) {
    // Swallow: telemetry is best-effort.
  }
};

const wait = (milliseconds) => new Promise((resolve) => {
  setTimeout(resolve, milliseconds);
});

/**
 * Runs an async operation, retrying only failures that are transient by nature
 * (offline, deadline exceeded, cancelled). Everything else fails immediately, so
 * genuine problems are never hidden behind a delay.
 *
 * @param {Function} fn - The async operation to run
 * @param {{ attempts?: number, delays?: number[] }} [options]
 * @returns {Promise<*>} The resolved value of `fn`
 */
export const runWithTransientRetry = async (fn, { attempts = 3, delays = RETRY_DELAYS } = {}) => {
  const totalAttempts = Math.max(1, attempts);
  let lastError;

  for (let attempt = 0; attempt < totalAttempts; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      const isLastAttempt = attempt === totalAttempts - 1;
      if (isLastAttempt || !isRetryableError(error)) {
        throw error;
      }

      const delay = delays[Math.min(attempt, delays.length - 1)] || 0;
      if (delay > 0) await wait(delay);
    }
  }

  throw lastError;
};